import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Row,
  Col,
  App,
  Upload,
  Image,
  Switch,
  Select
} from 'antd';
import type { TabsProps } from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  BugOutlined,
  ContactsOutlined,
  HomeOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  PictureOutlined,
  DeleteOutlined,
  NotificationOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteSettingsApi, uploadApi } from '../../services/api';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const AdminSiteSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [heroBgEnType, setHeroBgEnType] = useState<'image' | 'video'>('image');
  const [heroBgZhType, setHeroBgZhType] = useState<'image' | 'video'>('image');
  const queryClient = useQueryClient();
  const { message: messageApi, modal } = App.useApp();

  // Fetch site settings
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: siteSettingsApi.getSettings
  });

  const settings = settingsData?.settings || null;

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadLoading(true);
    try {
      const response = await uploadApi.uploadImage(file);
      messageApi.success('Image uploaded successfully');
      return response.file.url;
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || 'Failed to upload image');
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: siteSettingsApi.updateContact,
    onSuccess: () => {
      messageApi.success('Contact information updated successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update contact information');
    }
  });

  // Update logo mutation
  const updateLogoMutation = useMutation({
    mutationFn: siteSettingsApi.updateLogo,
    onSuccess: () => {
      messageApi.success('Logo updated successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update logo');
    }
  });

  // Update popup notice mutation
  const updatePopupNoticeMutation = useMutation({
    mutationFn: siteSettingsApi.updatePopupNotice,
    onSuccess: () => {
      messageApi.success('Popup notice updated successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update popup notice');
    }
  });

  // Update page content mutation
  const updatePageContentMutation = useMutation({
    mutationFn: siteSettingsApi.updatePageContent,
    onSuccess: () => {
      modal.success({
        title: 'Banner content saved',
        content: 'Your banner changes have been saved and published to the site.',
      });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      // Log server response for debugging
      // eslint-disable-next-line no-console
      console.error('Page content update error:', error?.response?.data || error);
      messageApi.error(error.response?.data?.message || 'Failed to update page content');
    }
  });

  // Update hero mutation
  const updateHeroMutation = useMutation({
    mutationFn: async (data: any) => {
      // Merge with existing hero data to ensure all required fields are present
      const existingHero = settings?.hero || {};
      const completeHeroData = {
        title: data.title || existingHero.title || { en: '', zh: '' },
        subtitle: data.subtitle || existingHero.subtitle || { en: '', zh: '' },
        backgroundImage: data.backgroundImage !== undefined
          ? data.backgroundImage
          : (existingHero.backgroundImage || '/images/hero-bg.jpg'),
        // New localized background media support
        background: data.background || existingHero.background,
        ctaText: existingHero.ctaText || { en: 'Explore Products', zh: '瀏覽產品' },
        ctaLink: existingHero.ctaLink || '/products'
      };
      return siteSettingsApi.updateHero(completeHeroData);
    },
    onSuccess: () => {
      messageApi.success('Hero section updated successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update hero section');
    }
  });

  // For general settings, create a safe update that sends complete data
  const updateGeneralMutation = useMutation({
    mutationFn: async (partialData: any) => {
      // Send the full settings object with only the general fields updated
      const completeData = {
        siteName: partialData.siteName || settings?.siteName || { en: '', zh: '' },
        tagline: partialData.tagline || settings?.tagline || { en: '', zh: '' },
        omronConnect: partialData.omronConnect || settings?.omronConnect || {
          title: { en: 'OMRON connect', zh: 'OMRON connect' },
          subtitle: { en: 'OMRON connect makes it easy to view, save, and manage your health data.', zh: 'OMRON connect 讓您輕鬆查看、儲存和管理您的健康數據。' },
          buttonText: { en: 'Find out more', zh: '了解更多' },
          buttonLink: 'https://www.omronconnect.com/au/en_gb/',
          banner: { en: { imageUrl: '' }, zh: { imageUrl: '' } }
        },
        logo: settings?.logo || {
          url: '',
          alt: { en: 'Logo', zh: '標誌' }
        },
        contact: settings?.contact || {
          phone: '',
          email: '',
          supportEmail: '',
          address: { en: '', zh: '' },
          emergencyNumber: '',
          medicalHelpline: ''
        },
        businessHours: partialData.businessHours || settings?.businessHours || {
          weekdays: '',
          weekends: '',
          timezone: '',
          note: { en: '', zh: '' }
        },
        socialMedia: settings?.socialMedia || {
          facebook: '',
          instagram: '',
          linkedin: '',
          youtube: '',
          twitter: ''
        },
        hero: settings?.hero || {
          title: { en: '', zh: '' },
          subtitle: { en: '', zh: '' },
          backgroundImage: '',
          ctaText: { en: '', zh: '' },
          ctaLink: ''
        },
        features: settings?.features || [],
        about: settings?.about || {
          title: { en: '', zh: '' },
          content: { en: '', zh: '' },
          mission: { en: '', zh: '' },
          vision: { en: '', zh: '' }
        },
        support: settings?.support || {
          responseTime: { en: '', zh: '' },
          downloadNote: { en: '', zh: '' }
        },
        footer: settings?.footer || {
          copyright: { en: '', zh: '' },
          description: { en: '', zh: '' }
        },
        seo: settings?.seo || {
          defaultTitle: { en: '', zh: '' },
          defaultDescription: { en: '', zh: '' },
          keywords: { en: '', zh: '' }
        },
        regions: settings?.regions || [],
        emergency: settings?.emergency || {
          title: { en: '', zh: '' },
          message: { en: '', zh: '' }
        },
        popupNotice: settings?.popupNotice || {
          isActive: false,
          title: { en: '', zh: '' },
          description: { en: '', zh: '' },
          imageUrl: '',
          frequency: 'once-per-day'
        }
      };
      return siteSettingsApi.updateSettings(completeData);
    },
    onSuccess: () => {
      messageApi.success('General settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update general settings');
    }
  });

  // Seed settings mutation
  const seedMutation = useMutation({
    mutationFn: siteSettingsApi.seedSettings,
    onSuccess: () => {
      messageApi.success('Site settings initialized successfully');
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to initialize settings');
    }
  });

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(settings);
      const enType = settings?.hero?.background?.en?.mediaType as 'image' | 'video' | undefined;
      const zhType = settings?.hero?.background?.zh?.mediaType as 'image' | 'video' | undefined;
      if (enType) setHeroBgEnType(enType);
      if (zhType) setHeroBgZhType(zhType);
    }
  }, [settings, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Ensure we capture values set programmatically via setFieldsValue (including unregistered fields)
      const allValues = form.getFieldsValue(true);
      if (activeTab === 'general') {
        const generalData = {
          siteName: values.siteName,
          tagline: values.tagline,
          businessHours: values.businessHours,
          // Use full form state to include fields set programmatically (e.g., uploaded image URLs)
          omronConnect: (allValues.omronConnect || values.omronConnect)
        };
        await updateGeneralMutation.mutateAsync(generalData);
      } else if (activeTab === 'contact') {
        const contactData = {
          phone: values.contact?.phone || '',
          email: values.contact?.email || '',
          supportEmail: values.contact?.supportEmail || '',
          address: settings?.contact?.address || { en: '', zh: '' },
          emergencyNumber: values.contact?.emergencyNumber || '',
          medicalHelpline: values.contact?.medicalHelpline || ''
        };
        await updateContactMutation.mutateAsync(contactData);
      } else if (activeTab === 'homepage') {
        // Collect hero fields including new localized background media
        const allValues = form.getFieldsValue(true);
        const heroValues = allValues.hero || values.hero || {};

        // Ensure media removal is persisted by explicitly sending empty strings when cleared
        const normalizeHeroBackground = (bg: any) => {
          if (!bg) return bg;
          const result: any = { ...bg };
          ['en','zh'].forEach((loc) => {
            if (result[loc]) {
              const mt = result[loc].mediaType;
              const img = result[loc].imageUrl;
              const vid = result[loc].videoUrl;
              if ((mt === 'image' && !img) || (mt === 'video' && !vid) || (!img && !vid)) {
                result[loc] = { ...result[loc], imageUrl: '', videoUrl: '' };
              }
            }
          });
          return result;
        };

        const heroData = {
          title: heroValues.title,
          subtitle: heroValues.subtitle,
          background: normalizeHeroBackground(heroValues.background)
        };
        await updateHeroMutation.mutateAsync(heroData);
      } else if (activeTab === 'popup') {
        const popupData = {
          isActive: values.popupNotice?.isActive || false,
          title: values.popupNotice?.title || { en: '', zh: '' },
          description: values.popupNotice?.description || { en: '', zh: '' },
          imageUrl: values.popupNotice?.imageUrl || settings?.popupNotice?.imageUrl || '',
          frequency: values.popupNotice?.frequency || 'once-per-session'
        };
        await updatePopupNoticeMutation.mutateAsync(popupData);
      } else if (activeTab === 'pages') {
        // Handle page content updates
        // Normalize banner media so removals overwrite existing values on save
        const normalizeBannerMedia = (obj: any): any => {
          if (!obj || typeof obj !== 'object') return obj;
          const result: any = Array.isArray(obj) ? [...obj] : { ...obj };
          Object.keys(result).forEach((key) => {
            const value = result[key];
            if (key === 'banner' && value && typeof value === 'object') {
              ['en','zh'].forEach((loc) => {
                if (value[loc] && typeof value[loc] === 'object') {
                  const mediaType = value[loc].mediaType;
                  const imageUrl = value[loc].imageUrl;
                  const videoUrl = value[loc].videoUrl;
                  const isEmptyImage = !imageUrl || imageUrl === '';
                  const isEmptyVideo = !videoUrl || videoUrl === '';
                  // If user removed or field missing per selected type, clear both to ensure overwrite
                  if ((mediaType === 'image' && isEmptyImage) || (mediaType === 'video' && isEmptyVideo) || (isEmptyImage && isEmptyVideo)) {
                    result[key][loc] = {
                      ...value[loc],
                      imageUrl: '',
                      videoUrl: ''
                    };
                  }
                }
              });
            } else if (value && typeof value === 'object') {
              result[key] = normalizeBannerMedia(value);
            }
          });
          return result;
        };

        // Sanitize payload to remove undefined/null values and empty containers
        const sanitizeObject = (obj: any): any => {
          if (obj === null || obj === undefined) return undefined;
          if (Array.isArray(obj)) {
            const cleaned = obj
              .map((v) => sanitizeObject(v))
              .filter((v) => v !== undefined);
            return cleaned.length ? cleaned : undefined;
          }
          if (typeof obj === 'object') {
            const result: any = {};
            Object.keys(obj).forEach((key) => {
              const cleaned = sanitizeObject(obj[key]);
              if (cleaned !== undefined) {
                result[key] = cleaned;
              }
            });
            return Object.keys(result).length ? result : undefined;
          }
          return obj;
        };

        const pageDataRaw = allValues.pages || values.pages || {};
        const normalized = normalizeBannerMedia(pageDataRaw);
        const sanitized = sanitizeObject(normalized) || {};
        await updatePageContentMutation.mutateAsync(sanitized);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedSettings = () => {
    modal.confirm({
      title: 'Initialize Site Settings',
      content: 'This will create default site settings with sample data. This action can only be done once.',
      icon: <ExclamationCircleOutlined />,
      onOk: () => seedMutation.mutate()
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!settings) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Site Settings Not Initialized</Title>
          <Paragraph>
            Site settings need to be initialized before you can manage them.
          </Paragraph>
          <Button 
            type="primary" 
            icon={<BugOutlined />}
            loading={seedMutation.isPending}
            onClick={handleSeedSettings}
          >
            Initialize Site Settings
          </Button>
        </div>
      </Card>
    );
  }

  const getCurrentMutation = () => {
    switch (activeTab) {
      case 'general':
        return updateGeneralMutation;
      case 'contact':
        return updateContactMutation;
      case 'homepage':
        return updateHeroMutation;
      case 'logo':
        return updateLogoMutation;
      case 'popup':
        return updatePopupNoticeMutation;
      case 'pages':
        return updatePageContentMutation;
      default:
        return updateGeneralMutation;
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <div>
          <Row gutter={24}>
            <Col span={12}>
              <Card title="Site Information" size="small">
                <Form.Item label="Site Name (English)" name={['siteName', 'en']}>
                  <Input />
                </Form.Item>
                <Form.Item label="Site Name (Chinese)" name={['siteName', 'zh']}>
                  <Input />
                </Form.Item>
                <Form.Item label="Tagline (English)" name={['tagline', 'en']}>
                  <Input />
                </Form.Item>
                <Form.Item label="Tagline (Chinese)" name={['tagline', 'zh']}>
                  <Input />
                </Form.Item>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Site Logo" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Title level={5}>Current Logo</Title>
                  {settings?.logo?.url ? (
                    <div style={{ 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 6, 
                      overflow: 'hidden',
                      marginBottom: 16,
                      maxWidth: 200
                    }}>
                      <Image
                        src={settings.logo.url}
                        alt={settings.logo.alt?.en || 'Logo'}
                        style={{ width: '100%', height: 'auto', maxHeight: 100, objectFit: 'contain' }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7nOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg="
                      />
                    </div>
                  ) : (
                    <div style={{ 
                      border: '2px dashed #d9d9d9', 
                      borderRadius: 6, 
                      padding: 40, 
                      textAlign: 'center',
                      color: '#999',
                      marginBottom: 16
                    }}>
                      <PictureOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                      <div>No logo uploaded</div>
                    </div>
                  )}
                </div>
                
                <Upload
                  name="image"
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    try {
                      const imageUrl = await handleImageUpload(file);
                      
                      // Update logo via API
                      const logoData = {
                        url: imageUrl,
                        alt: {
                          en: settings?.logo?.alt?.en || 'Logo',
                          zh: settings?.logo?.alt?.zh || '標誌'
                        }
                      };
                      
                      await updateLogoMutation.mutateAsync(logoData);
                      
                      // Also update the form field
                      form.setFieldValue(['logo', 'url'], imageUrl);
                      
                    } catch (error) {
                      console.error('Failed to upload logo:', error);
                    }
                    return false; // Prevent default upload behavior
                  }}
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploadLoading}
                    size="large"
                    type="primary"
                    style={{ marginBottom: 16 }}
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </Upload>

                {settings?.logo?.url && (
                  <div style={{ marginBottom: 16 }}>
                    <Button 
                      icon={<DeleteOutlined />} 
                      danger
                      onClick={async () => {
                        try {
                          const logoData = {
                            url: '',
                            alt: {
                              en: 'Logo',
                              zh: '標誌'
                            }
                          };
                          await updateLogoMutation.mutateAsync(logoData);
                          messageApi.success('Logo removed');
                        } catch (error) {
                          messageApi.error('Failed to remove logo');
                        }
                      }}
                      loading={updateLogoMutation.isPending}
                    >
                      Remove Logo
                    </Button>
                  </div>
                )}
                
                <div>
                  <Form.Item label="Logo Alt Text (English)" name={['logo', 'alt', 'en']}>
                    <Input placeholder="Logo description" />
                  </Form.Item>
                  <Form.Item label="Logo Alt Text (Chinese)" name={['logo', 'alt', 'zh']}>
                    <Input placeholder="標誌描述" />
                  </Form.Item>
                </div>
              </Card>
            </Col>
          </Row>
          
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card title="OMRON connect Section" size="small">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Title (English)" name={['omronConnect','title','en']}>
                      <Input placeholder="OMRON connect" />
                    </Form.Item>
                    <Form.Item label="Title (Chinese)" name={['omronConnect','title','zh']}>
                      <Input placeholder="OMRON connect" />
                    </Form.Item>
                    <Form.Item label="Subtitle (English)" name={['omronConnect','subtitle','en']}>
                      <TextArea rows={2} placeholder="OMRON connect makes it easy to view, save, and manage your health data." />
                    </Form.Item>
                    <Form.Item label="Subtitle (Chinese)" name={['omronConnect','subtitle','zh']}>
                      <TextArea rows={2} placeholder="OMRON connect 讓您輕鬆查看、儲存和管理您的健康數據。" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Button Text (English)" name={['omronConnect','buttonText','en']}>
                      <Input placeholder="Find out more" />
                    </Form.Item>
                    <Form.Item label="Button Text (Chinese)" name={['omronConnect','buttonText','zh']}>
                      <Input placeholder="了解更多" />
                    </Form.Item>
                    <Form.Item label="Button Link" name={['omronConnect','buttonLink']}>
                      <Input placeholder="https://www.omronconnect.com/au/en_gb/" />
                    </Form.Item>
                    <Form.Item label="Banner Image (English)">
                      <Upload accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const { file: f } = await uploadApi.uploadImage(file); const url = f.url; const current = form.getFieldValue(['omronConnect','banner','en']) || {}; form.setFieldsValue({ omronConnect: { banner: { en: { ...current, imageUrl: url } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload EN Image</Button>
                      </Upload>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const v = getFieldValue(['omronConnect','banner','en','imageUrl']);
                          return v ? (
                            <div style={{ marginTop: 8 }}>
                              <Image src={v} alt="EN Banner" style={{ width: '100%', maxHeight: 160, objectFit: 'cover' }} />
                              <div style={{ marginTop: 8 }}>
                                <Button danger size="small" onClick={()=>{ const cur = getFieldValue(['omronConnect','banner','en']) || {}; form.setFieldsValue({ omronConnect: { banner: { en: { ...cur, imageUrl: '' } } } }); }}>Remove</Button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 8, color: '#999' }}>Using default blue background</div>
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                    <Form.Item label="Banner Image (Chinese)">
                      <Upload accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const { file: f } = await uploadApi.uploadImage(file); const url = f.url; const current = form.getFieldValue(['omronConnect','banner','zh']) || {}; form.setFieldsValue({ omronConnect: { banner: { zh: { ...current, imageUrl: url } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload ZH Image</Button>
                      </Upload>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const v = getFieldValue(['omronConnect','banner','zh','imageUrl']);
                          return v ? (
                            <div style={{ marginTop: 8 }}>
                              <Image src={v} alt="ZH Banner" style={{ width: '100%', maxHeight: 160, objectFit: 'cover' }} />
                              <div style={{ marginTop: 8 }}>
                                <Button danger size="small" onClick={()=>{ const cur = getFieldValue(['omronConnect','banner','zh']) || {}; form.setFieldsValue({ omronConnect: { banner: { zh: { ...cur, imageUrl: '' } } } }); }}>Remove</Button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 8, color: '#999' }}>Using default blue background</div>
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card title="Business Hours" size="small">
                <Form.Item label="Weekdays" name={['businessHours', 'weekdays']}>
                  <Input placeholder="9:00 AM - 6:00 PM" />
                </Form.Item>
                <Form.Item label="Weekends" name={['businessHours', 'weekends']}>
                  <Input placeholder="Closed" />
                </Form.Item>
                <Form.Item label="Timezone" name={['businessHours', 'timezone']}>
                  <Input placeholder="Hong Kong Time" />
                </Form.Item>
                <Form.Item label="Note (English)" name={['businessHours', 'note', 'en']}>
                  <Input placeholder="Monday - Friday" />
                </Form.Item>
                <Form.Item label="Note (Chinese)" name={['businessHours', 'note', 'zh']}>
                  <Input placeholder="星期一至五" />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'contact',
      label: (
        <span>
          <ContactsOutlined />
          Contact
        </span>
      ),
      children: (
        <Row gutter={24}>
          <Col span={24}>
            <Card title="Contact Information" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Phone" name={['contact', 'phone']}>
                    <Input />
                  </Form.Item>
                  <Form.Item label="Email" name={['contact', 'email']}>
                    <Input type="email" />
                  </Form.Item>
                  <Form.Item label="Support Email" name={['contact', 'supportEmail']}>
                    <Input type="email" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Emergency Number" name={['contact', 'emergencyNumber']}>
                    <Input />
                  </Form.Item>
                  <Form.Item label="Medical Helpline" name={['contact', 'medicalHelpline']}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: 'homepage',
      label: (
        <span>
          <HomeOutlined />
          Homepage
        </span>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="Hero Section" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Title (English)" name={['hero', 'title', 'en']}>
                  <Input />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['hero', 'title', 'zh']}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['hero', 'subtitle', 'en']}>
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['hero', 'subtitle', 'zh']}>
                  <TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Hero Background" size="small">
            <Row gutter={16}>
              <Col span={24}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['hero','background','en','mediaType']} initialValue={settings?.hero?.background?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} value={heroBgEnType} onChange={(v) => { setHeroBgEnType(v as 'image'|'video'); const current = form.getFieldValue(['hero','background','en']) || {}; form.setFieldsValue({ hero: { background: { en: { ...current, mediaType: v } } } }); }} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (getFieldValue(['hero','background','en','mediaType']) || heroBgEnType) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload accept="image/*" showUploadList={false} beforeUpload={async (file) => { const res = await uploadApi.uploadImage(file); const url = res.file.url; const current = form.getFieldValue(['hero','background','en']) || {}; form.setFieldsValue({ hero: { background: { en: { ...current, imageUrl: url, videoUrl: '' } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const v = form.getFieldValue(['hero','background','en','imageUrl']) || settings?.hero?.background?.en?.imageUrl || settings?.hero?.backgroundImage; return v ? (<div style={{ marginTop: 8 }}><img src={v} alt="Preview" style={{ maxWidth: '100%', border: '1px solid #f0f0f0', borderRadius: 6 }} /><div style={{ marginTop: 8 }}><Button danger size="small" onClick={() => { const cur = form.getFieldValue(['hero','background','en']) || {}; form.setFieldsValue({ hero: { background: { en: { ...cur, imageUrl: '', videoUrl: '' } } } }); }}>Remove</Button></div></div>) : (<div style={{ marginTop: 8, color: '#999' }}>Using default</div>); })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (getFieldValue(['hero','background','en','mediaType']) || heroBgEnType) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload accept="video/*" showUploadList={false} beforeUpload={async (file) => { const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = form.getFieldValue(['hero','background','en']) || {}; form.setFieldsValue({ hero: { background: { en: { ...current, videoUrl: url, imageUrl: '' } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                  </Upload>
                      {(() => { const v = form.getFieldValue(['hero','background','en','videoUrl']) || settings?.hero?.background?.en?.videoUrl; return v ? (<div style={{ marginTop: 8 }}><video src={v} controls style={{ width: '100%', border: '1px solid #f0f0f0', borderRadius: 6 }} /><div style={{ marginTop: 8 }}><Button danger size="small" onClick={() => { const cur = form.getFieldValue(['hero','background','en']) || {}; form.setFieldsValue({ hero: { background: { en: { ...cur, videoUrl: '', imageUrl: '' } } } }); }}>Remove</Button></div></div>) : (<div style={{ marginTop: 8, color: '#999' }}>Using default</div>); })()}
                    </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['hero','background','zh','mediaType']} initialValue={settings?.hero?.background?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} value={heroBgZhType} onChange={(v) => { setHeroBgZhType(v as 'image'|'video'); const current = form.getFieldValue(['hero','background','zh']) || {}; form.setFieldsValue({ hero: { background: { zh: { ...current, mediaType: v } } } }); }} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (getFieldValue(['hero','background','zh','mediaType']) || heroBgZhType) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload accept="image/*" showUploadList={false} beforeUpload={async (file) => { const res = await uploadApi.uploadImage(file); const url = res.file.url; const current = form.getFieldValue(['hero','background','zh']) || {}; form.setFieldsValue({ hero: { background: { zh: { ...current, imageUrl: url, videoUrl: '' } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const v = form.getFieldValue(['hero','background','zh','imageUrl']) || settings?.hero?.background?.zh?.imageUrl || settings?.hero?.backgroundImage; return v ? (<div style={{ marginTop: 8 }}><img src={v} alt="Preview" style={{ maxWidth: '100%', border: '1px solid #f0f0f0', borderRadius: 6 }} /><div style={{ marginTop: 8 }}><Button danger size="small" onClick={() => { const cur = form.getFieldValue(['hero','background','zh']) || {}; form.setFieldsValue({ hero: { background: { zh: { ...cur, imageUrl: '', videoUrl: '' } } } }); }}>Remove</Button></div></div>) : (<div style={{ marginTop: 8, color: '#999' }}>Using default</div>); })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (getFieldValue(['hero','background','zh','mediaType']) || heroBgZhType) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload accept="video/*" showUploadList={false} beforeUpload={async (file) => { const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = form.getFieldValue(['hero','background','zh']) || {}; form.setFieldsValue({ hero: { background: { zh: { ...current, videoUrl: url, imageUrl: '' } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const v = form.getFieldValue(['hero','background','zh','videoUrl']) || settings?.hero?.background?.zh?.videoUrl; return v ? (<div style={{ marginTop: 8 }}><video src={v} controls style={{ width: '100%', border: '1px solid #f0f0f0', borderRadius: 6 }} /><div style={{ marginTop: 8 }}><Button danger size="small" onClick={() => { const cur = form.getFieldValue(['hero','background','zh']) || {}; form.setFieldsValue({ hero: { background: { zh: { ...cur, videoUrl: '', imageUrl: '' } } } }); }}>Remove</Button></div></div>) : (<div style={{ marginTop: 8, color: '#999' }}>Using default</div>); })()}
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Space>
      ),
    },
    {
      key: 'popup',
      label: (
        <span>
          <NotificationOutlined />
          Popup Notice
        </span>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="Popup Notice Configuration" size="small">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Enable Popup Notice" name={['popupNotice', 'isActive']} valuePropName="checked">
                  <Switch 
                    checkedChildren="ON" 
                    unCheckedChildren="OFF"
                    style={{ marginBottom: 16 }}
                  />
                </Form.Item>
                
                <Form.Item label="Display Frequency" name={['popupNotice', 'frequency']}>
                  <Select placeholder="Select display frequency">
                    <Select.Option value="always">Always show (every page visit)</Select.Option>
                    <Select.Option value="once-per-session">Once per session</Select.Option>
                    <Select.Option value="once-per-day">Once per day</Select.Option>
                    <Select.Option value="once-per-week">Once per week</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Title (English)" name={['popupNotice', 'title', 'en']}>
                  <Input placeholder="Enter popup title in English" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['popupNotice', 'title', 'zh']}>
                  <Input placeholder="輸入中文彈窗標題" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Description (English)" name={['popupNotice', 'description', 'en']}>
                  <TextArea rows={3} placeholder="Enter popup description in English" />
                </Form.Item>
                <Form.Item label="Description (Chinese)" name={['popupNotice', 'description', 'zh']}>
                  <TextArea rows={3} placeholder="輸入中文彈窗描述" />
                </Form.Item>
              </Col>
            </Row>
            
            {/* Hidden field to track imageUrl */}
            <Form.Item name={['popupNotice', 'imageUrl']} hidden>
              <Input />
            </Form.Item>
          </Card>

          <Card title="Popup Image" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Title level={5}>Current Popup Image</Title>
                  {settings?.popupNotice?.imageUrl ? (
                    <div style={{ 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 6, 
                      overflow: 'hidden',
                      marginBottom: 16 
                    }}>
                      <Image
                        src={settings.popupNotice.imageUrl}
                        alt="Popup Notice Image"
                        style={{ width: '100%', height: 200, objectFit: 'cover' }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7nOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg="
                      />
                    </div>
                  ) : (
                    <div style={{ 
                      border: '2px dashed #d9d9d9', 
                      borderRadius: 6, 
                      padding: 40, 
                      textAlign: 'center',
                      color: '#999',
                      marginBottom: 16
                    }}>
                      <PictureOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                      <div>No popup image uploaded</div>
                    </div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Title level={5}>Upload Popup Image</Title>
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Upload an image for the popup notice. 
                    Recommended size: 400x300px or similar ratio. 
                    Supported formats: JPG, PNG, WebP
                  </Paragraph>
                  
                  <Upload
                    name="image"
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      try {
                        const imageUrl = await handleImageUpload(file);
                        
                        // Update popup image via API
                        const existingPopup = settings?.popupNotice || {};
                        const updatedPopup = {
                          ...existingPopup,
                          imageUrl: imageUrl
                        };
                        
                        await updatePopupNoticeMutation.mutateAsync(updatedPopup);
                        
                        // Also update the form field so it doesn't get lost on save
                        form.setFieldValue(['popupNotice', 'imageUrl'], imageUrl);
                        
                      } catch (error) {
                        console.error('Failed to upload popup image:', error);
                      }
                      return false; // Prevent default upload behavior
                    }}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={uploadLoading}
                      size="large"
                      type="primary"
                    >
                      {uploadLoading ? 'Uploading...' : 'Upload Popup Image'}
                    </Button>
                  </Upload>

                  {settings?.popupNotice?.imageUrl && (
                    <div style={{ marginTop: 16 }}>
                      <Button 
                        icon={<DeleteOutlined />} 
                        danger
                        onClick={async () => {
                          try {
                            const existingPopup = settings?.popupNotice || {};
                            const updatedPopup = {
                              ...existingPopup,
                              imageUrl: ''
                            };
                            await updatePopupNoticeMutation.mutateAsync(updatedPopup);
                            
                            // Also update the form field
                            form.setFieldValue(['popupNotice', 'imageUrl'], '');
                            
                            messageApi.success('Popup image removed');
                          } catch (error) {
                            messageApi.error('Failed to remove popup image');
                          }
                        }}
                        loading={updatePopupNoticeMutation.isPending}
                      >
                        Remove Popup Image
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Space>
      ),
    },
    {
      key: 'pages',
      label: (
        <span>
          <FileImageOutlined />
          Banner Content
        </span>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 1) Home Products (Category) */}
          <Card title="Home Products Page Content" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['pages','categories','homeProducts','banner','en','mediaType']} initialValue={settings?.pages?.categories?.homeProducts?.banner?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','homeProducts','banner','en','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.categories?.homeProducts?.banner?.en || {}; form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ en: { ...current, imageUrl:url, mediaType:'image' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const fv = form.getFieldValue(['pages','categories','homeProducts','banner','en','imageUrl']); const img = fv !== undefined ? fv : settings?.pages?.categories?.homeProducts?.banner?.en?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','homeProducts','banner','en','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.categories?.homeProducts?.banner?.en || {}; form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ en: { ...current, videoUrl:url, mediaType:'video' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const fv = form.getFieldValue(['pages','categories','homeProducts','banner','en','videoUrl']); const vid = fv !== undefined ? fv : settings?.pages?.categories?.homeProducts?.banner?.en?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['pages','categories','homeProducts','banner','zh','mediaType']} initialValue={settings?.pages?.categories?.homeProducts?.banner?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','homeProducts','banner','zh','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.categories?.homeProducts?.banner?.zh || {}; form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ zh: { ...current, imageUrl:url, mediaType:'image' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const fv = form.getFieldValue(['pages','categories','homeProducts','banner','zh','imageUrl']); const img = fv !== undefined ? fv : settings?.pages?.categories?.homeProducts?.banner?.zh?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','homeProducts','banner','zh','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.categories?.homeProducts?.banner?.zh || {}; form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ zh: { ...current, videoUrl:url, mediaType:'video' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const fv = form.getFieldValue(['pages','categories','homeProducts','banner','zh','videoUrl']); const vid = fv !== undefined ? fv : settings?.pages?.categories?.homeProducts?.banner?.zh?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ homeProducts:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                
                <Form.Item label="Title (English)" name={['pages', 'categories', 'homeProducts', 'title', 'en']}>
                  <Input placeholder="Home Health Products" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['pages', 'categories', 'homeProducts', 'title', 'zh']}>
                  <Input placeholder="家用健康產品" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['pages', 'categories', 'homeProducts', 'subtitle', 'en']}>
                  <TextArea rows={2} placeholder="Professional healthcare monitoring for your home" />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['pages', 'categories', 'homeProducts', 'subtitle', 'zh']}>
                  <TextArea rows={2} placeholder="為您的家庭提供專業的醫療監測" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 2) Medical Products (Category) */}
          <Card title="Medical Products Page Content" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['pages','categories','medicalProducts','banner','en','mediaType']} initialValue={settings?.pages?.categories?.medicalProducts?.banner?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','medicalProducts','banner','en','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.categories?.medicalProducts?.banner?.en || {}; form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ en: { ...current, imageUrl:url, mediaType:'image' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','categories','medicalProducts','banner','en','imageUrl']) || settings?.pages?.categories?.medicalProducts?.banner?.en?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','medicalProducts','banner','en','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.categories?.medicalProducts?.banner?.en || {}; form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ en: { ...current, videoUrl:url, mediaType:'video' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','categories','medicalProducts','banner','en','videoUrl']) || settings?.pages?.categories?.medicalProducts?.banner?.en?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['pages','categories','medicalProducts','banner','zh','mediaType']} initialValue={settings?.pages?.categories?.medicalProducts?.banner?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','medicalProducts','banner','zh','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.categories?.medicalProducts?.banner?.zh || {}; form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ zh: { ...current, imageUrl:url, mediaType:'image' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','categories','medicalProducts','banner','zh','imageUrl']) || settings?.pages?.categories?.medicalProducts?.banner?.zh?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','categories','medicalProducts','banner','zh','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.categories?.medicalProducts?.banner?.zh || {}; form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ zh: { ...current, videoUrl:url, mediaType:'video' } } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','categories','medicalProducts','banner','zh','videoUrl']) || settings?.pages?.categories?.medicalProducts?.banner?.zh?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ categories:{ medicalProducts:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                
                <Form.Item label="Title (English)" name={['pages', 'categories', 'medicalProducts', 'title', 'en']}>
                  <Input placeholder="Medical Products" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['pages', 'categories', 'medicalProducts', 'title', 'zh']}>
                  <Input placeholder="醫療產品" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['pages', 'categories', 'medicalProducts', 'subtitle', 'en']}>
                  <TextArea rows={2} placeholder="Clinical-grade devices for healthcare professionals" />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['pages', 'categories', 'medicalProducts', 'subtitle', 'zh']}>
                  <TextArea rows={2} placeholder="為醫療專業人員提供的臨床級設備" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 3) Stores (Store Locator) */}
          <Card title="Stores Page Content" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['pages','storeLocator','banner','en','mediaType']} initialValue={settings?.pages?.storeLocator?.banner?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','storeLocator','banner','en','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.storeLocator?.banner?.en || {}; form.setFieldsValue({ pages:{ storeLocator:{ banner:{ en: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','storeLocator','banner','en','imageUrl']) || settings?.pages?.storeLocator?.banner?.en?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ storeLocator:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','storeLocator','banner','en','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.storeLocator?.banner?.en || {}; form.setFieldsValue({ pages:{ storeLocator:{ banner:{ en: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','storeLocator','banner','en','videoUrl']) || settings?.pages?.storeLocator?.banner?.en?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ storeLocator:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['pages','storeLocator','banner','zh','mediaType']} initialValue={settings?.pages?.storeLocator?.banner?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','storeLocator','banner','zh','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.storeLocator?.banner?.zh || {}; form.setFieldsValue({ pages:{ storeLocator:{ banner:{ zh: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','storeLocator','banner','zh','imageUrl']) || settings?.pages?.storeLocator?.banner?.zh?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ storeLocator:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','storeLocator','banner','zh','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.storeLocator?.banner?.zh || {}; form.setFieldsValue({ pages:{ storeLocator:{ banner:{ zh: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','storeLocator','banner','zh','videoUrl']) || settings?.pages?.storeLocator?.banner?.zh?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ storeLocator:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                
                <Form.Item label="Title (English)" name={['pages', 'storeLocator', 'title', 'en']}>
                  <Input placeholder="Stores" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['pages', 'storeLocator', 'title', 'zh']}>
                  <Input placeholder="門市" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['pages', 'storeLocator', 'subtitle', 'en']}>
                  <TextArea rows={2} placeholder="Find our stores and partners near you" />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['pages', 'storeLocator', 'subtitle', 'zh']}>
                  <TextArea rows={2} placeholder="尋找離您最近的門市與合作夥伴" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 4) Service */}
          <Card title="Service Page Content" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['pages','service','banner','en','mediaType']} initialValue={settings?.pages?.service?.banner?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','service','banner','en','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.service?.banner?.en || {}; form.setFieldsValue({ pages:{ service:{ banner:{ en: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','service','banner','en','imageUrl']) || settings?.pages?.service?.banner?.en?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ service:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','service','banner','en','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.service?.banner?.en || {}; form.setFieldsValue({ pages:{ service:{ banner:{ en: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','service','banner','en','videoUrl']) || settings?.pages?.service?.banner?.en?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ service:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['pages','service','banner','zh','mediaType']} initialValue={settings?.pages?.service?.banner?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','service','banner','zh','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.service?.banner?.zh || {}; form.setFieldsValue({ pages:{ service:{ banner:{ zh: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','service','banner','zh','imageUrl']) || settings?.pages?.service?.banner?.zh?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ service:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','service','banner','zh','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.service?.banner?.zh || {}; form.setFieldsValue({ pages:{ service:{ banner:{ zh: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','service','banner','zh','videoUrl']) || settings?.pages?.service?.banner?.zh?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ service:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                
                <Form.Item label="Title (English)" name={['pages', 'service', 'title', 'en']}>
                  <Input placeholder="Service" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['pages', 'service', 'title', 'zh']}>
                  <Input placeholder="服務" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['pages', 'service', 'subtitle', 'en']}>
                  <TextArea rows={2} placeholder="Professional after-sales and maintenance support" />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['pages', 'service', 'subtitle', 'zh']}>
                  <TextArea rows={2} placeholder="專業售後與維修支援" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 5) About Us */}
          <Card title="About Page Content" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>English Banner</Title>
                <Form.Item label="Media Type" name={['pages','about','banner','en','mediaType']} initialValue={settings?.pages?.about?.banner?.en?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','about','banner','en','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.about?.banner?.en || {}; form.setFieldsValue({ pages:{ about:{ banner:{ en: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','about','banner','en','imageUrl']) || settings?.pages?.about?.banner?.en?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ about:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','about','banner','en','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.about?.banner?.en || {}; form.setFieldsValue({ pages:{ about:{ banner:{ en: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','about','banner','en','videoUrl']) || settings?.pages?.about?.banner?.en?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ about:{ banner:{ en:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                </Form.Item>
                  )}
                </Form.Item>

                <Title level={5} style={{ marginTop: 16 }}>Chinese Banner</Title>
                <Form.Item label="Media Type" name={['pages','about','banner','zh','mediaType']} initialValue={settings?.pages?.about?.banner?.zh?.mediaType || 'image'}>
                  <Select options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','about','banner','zh','mediaType']) !== 'video' && (
                    <Form.Item label="Upload Image">
                      <Upload name="image" accept="image/*" showUploadList={false} beforeUpload={async (file)=>{ const url = await handleImageUpload(file); const current = settings?.pages?.about?.banner?.zh || {}; form.setFieldsValue({ pages:{ about:{ banner:{ zh: { ...current, imageUrl:url, mediaType:'image' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Image</Button>
                      </Upload>
                      {(() => { const img = form.getFieldValue(['pages','about','banner','zh','imageUrl']) || settings?.pages?.about?.banner?.zh?.imageUrl; return img ? <div style={{marginTop:8}}><img src={img} alt="Preview" style={{maxWidth:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ about:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => getFieldValue(['pages','about','banner','zh','mediaType']) === 'video' && (
                    <Form.Item label="Upload Video">
                      <Upload name="video" accept="video/mp4,video/webm" showUploadList={false} beforeUpload={async (file)=>{ const res = await uploadApi.uploadVideo(file); const url = res.file.url; const current = settings?.pages?.about?.banner?.zh || {}; form.setFieldsValue({ pages:{ about:{ banner:{ zh: { ...current, videoUrl:url, mediaType:'video' } } } } }); return false; }}>
                        <Button icon={<UploadOutlined />} loading={uploadLoading}>Upload Video</Button>
                      </Upload>
                      {(() => { const vid = form.getFieldValue(['pages','about','banner','zh','videoUrl']) || settings?.pages?.about?.banner?.zh?.videoUrl; return vid ? <div style={{marginTop:8}}><video src={vid} controls style={{width:'100%', border:'1px solid #f0f0f0', borderRadius:6}} /><div style={{marginTop:8}}><Button danger size="small" onClick={()=>{ form.setFieldsValue({ pages:{ about:{ banner:{ zh:{ imageUrl:'', videoUrl:'' } } } } }); }}>Remove</Button></div></div> : <div style={{marginTop:8, color:'#999'}}>Using default</div>; })()}
                    </Form.Item>
                  )}
                </Form.Item>
                
                <Form.Item label="Title (English)" name={['pages', 'about', 'title', 'en']}>
                  <Input placeholder="About Our Healthcare Innovation" />
                </Form.Item>
                <Form.Item label="Title (Chinese)" name={['pages', 'about', 'title', 'zh']}>
                  <Input placeholder="關於我們的醫療創新" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Subtitle (English)" name={['pages', 'about', 'subtitle', 'en']}>
                  <TextArea rows={2} placeholder="For over 80 years, we have been committed to improving lives" />
                </Form.Item>
                <Form.Item label="Subtitle (Chinese)" name={['pages', 'about', 'subtitle', 'zh']}>
                  <TextArea rows={2} placeholder="80多年來，我們一直致力於改善生活" />
                </Form.Item>
                <Form.Item label="Mission Title (English)" name={['pages', 'about', 'missionTitle', 'en']}>
                  <Input placeholder="Our Mission" />
                </Form.Item>
                <Form.Item label="Mission Title (Chinese)" name={['pages', 'about', 'missionTitle', 'zh']}>
                  <Input placeholder="我們的使命" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Only the first five sections (aligned with navbar) are kept */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>Site Content Settings</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['siteSettings'] })}>
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />}
            loading={loading || getCurrentMutation().isPending}
            onClick={() => form.submit()}
          >
            Save {activeTab === 'general' ? 'General' : activeTab === 'contact' ? 'Contact' : activeTab === 'popup' ? 'Popup Notice' : activeTab === 'pages' ? 'Banner Content' : 'Homepage'} Settings
          </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={settings}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
      </Form>
    </div>
  );
};

export default AdminSiteSettings;
